import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization, MONTHS } from './_finHelpers';

// VA funding fee lookup — matches VA official 2023/2024 table
function fundingFeePct(eligibility, usedBefore, downPct, disability) {
  if (disability === 'yes') return 0;
  const first = usedBefore === 'no';
  if (eligibility === 'active') {
    if (downPct >= 10) return 1.4;
    if (downPct >= 5) return 1.65;
    return first ? 2.15 : 3.3;
  }
  if (eligibility === 'reservist') {
    if (downPct >= 10) return 1.4;
    if (downPct >= 5) return 1.65;
    return first ? 2.15 : 3.3;
  }
  return 0; // Surviving spouse
}

export default function VAMortgageCalc() {
  const [homePrice, setHomePrice] = useState(500000);
  const [down, setDown] = useState(0);
  const [downMode, setDownMode] = useState('%');
  const [term, setTerm] = useState(30);
  const [rate, setRate] = useState(6.625);
  const [eligibility, setEligibility] = useState('active');
  const [usedBefore, setUsedBefore] = useState('no');
  const [disability, setDisability] = useState('no');
  const [fundingFee, setFundingFee] = useState('financed'); // 'financed' | 'upfront'
  const [startMonth, setStartMonth] = useState(new Date().getMonth()+1);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [showMore, setShowMore] = useState(false);
  const [vaTaxInc, setVaTaxInc] = useState(0);
  const [vaInsInc, setVaInsInc] = useState(0);
  const [vaHoaInc, setVaHoaInc] = useState(0);
  const [vaOthInc, setVaOthInc] = useState(0);
  const [vaExtraM, setVaExtraM] = useState(0);
  const [vaExtraY, setVaExtraY] = useState(0);
  const [vaExtraOne, setVaExtraOne] = useState(0);
  const [vaBiweekly, setVaBiweekly] = useState(false);
  const [propTaxes, setPropTaxes] = useState(1.2);
  const [insurance, setInsurance] = useState(2500);
  const [hoa, setHoa] = useState(0);
  const [other, setOther] = useState(4000);

  const c = useMemo(() => {
    const downAmt = downMode === '%' ? homePrice * down/100 : down;
    const downPct = homePrice > 0 ? downAmt/homePrice*100 : 0;
    const baseLoan = Math.max(0, homePrice - downAmt);
    const ffPct = fundingFeePct(eligibility, usedBefore, downPct, disability);
    const ffAmt = baseLoan * ffPct / 100;
    const loan = fundingFee === 'financed' ? baseLoan + ffAmt : baseLoan;
    const n = term * 12;
    const amort = buildAmortization(loan, rate, n, startMonth, startYear);
    const monthlyTax = showMore ? homePrice * propTaxes/100/12 : 0;
    const monthlyIns = showMore ? insurance/12 : 0;
    const monthlyHoa = showMore ? hoa/12 : 0;
    const monthlyOther = showMore ? other/12 : 0;
    const monthlyTotal = amort.M + monthlyTax + monthlyIns + monthlyHoa + monthlyOther;
    return { downAmt, downPct, baseLoan, loan, ffPct, ffAmt, amort, monthlyTax, monthlyIns, monthlyHoa, monthlyOther, monthlyTotal };
  }, [homePrice, down, downMode, term, rate, eligibility, usedBefore, disability, fundingFee, showMore, propTaxes, insurance, hoa, other, startMonth, startYear]);

  const Radio = ({ name, value, cur, setter, label }) => (
    <label className="flex items-center gap-2 text-sm"><input type="radio" name={name} checked={cur===value} onChange={()=>setter(value)}/>{label}</label>
  );

  return (
    <>
      <CalcShell look="cnet" title="VA Mortgage Calculator" subtitle="Estimate VA loan payments including the VA funding fee for eligible veterans and service members."
        about={<>
          <p>The <b>VA loan</b> is a mortgage benefit for eligible active-duty service members, veterans, reservists/National Guard, and some surviving spouses. It requires no down payment and no PMI.</p>
          <p>Instead of PMI, borrowers pay a one-time <b>VA funding fee</b> that varies by service category, down payment, and whether it's a first or subsequent use.</p>
        </>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Home Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={homePrice} onChange={e=>setHomePrice(+e.target.value||0)} className="flex-1"/></div></div>
            <div className="field"><label>Down Payment</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step="0.01" value={down} onChange={e=>setDown(+e.target.value||0)}/>
                <select value={downMode} onChange={e=>setDownMode(e.target.value)}><option value="%">%</option><option value="$">$</option></select>
              </div>
            </div>
            <div className="field"><label>Loan Term</label><select value={term} onChange={e=>setTerm(+e.target.value)}><option value={30}>30 years</option><option value={20}>20 years</option><option value={15}>15 years</option></select></div>
            <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
            <div className="field"><label>VA Eligibility</label>
              <div className="flex flex-col gap-1 mt-1">
                <Radio name="elig" value="active" cur={eligibility} setter={setEligibility} label="Active/Veteran"/>
                <Radio name="elig" value="reservist" cur={eligibility} setter={setEligibility} label="Reservist/National Guard"/>
                <Radio name="elig" value="spouse" cur={eligibility} setter={setEligibility} label="Surviving Spouses"/>
              </div>
            </div>
            <div className="field"><label>Used VA Loan Before?</label>
              <div className="flex gap-4 mt-1"><Radio name="used" value="yes" cur={usedBefore} setter={setUsedBefore} label="Yes"/><Radio name="used" value="no" cur={usedBefore} setter={setUsedBefore} label="No"/></div>
            </div>
            <div className="field"><label>Service-Related Disability (10+%)?</label>
              <div className="flex gap-4 mt-1"><Radio name="dis" value="yes" cur={disability} setter={setDisability} label="Yes"/><Radio name="dis" value="no" cur={disability} setter={setDisability} label="No"/></div>
            </div>
            <div className="field"><label>VA Funding Fee</label>
              <div className="flex flex-col gap-1 mt-1">
                <Radio name="ff" value="financed" cur={fundingFee} setter={setFundingFee} label="Financed Into Loan"/>
                <Radio name="ff" value="upfront" cur={fundingFee} setter={setFundingFee} label="Paid Upfront"/>
              </div>
            </div>
            <button onClick={()=>setShowMore(!showMore)} className="text-blue-700 text-sm font-semibold mt-2" data-testid="va-more">{showMore?'− Hide':'+ More'} Options</button>
            {showMore && <div className="mt-3 space-y-2 border border-slate-200 rounded p-3 bg-slate-50/60">
              <div className="field !mb-2"><label>Property Taxes (%/yr)</label><input type="number" step="0.01" value={propTaxes} onChange={e=>setPropTaxes(+e.target.value||0)}/></div>
              <div className="field !mb-2"><label>Home Insurance ($/yr)</label><input type="number" value={insurance} onChange={e=>setInsurance(+e.target.value||0)}/></div>
              <div className="field !mb-2"><label>HOA Fee ($/yr)</label><input type="number" value={hoa} onChange={e=>setHoa(+e.target.value||0)}/></div>
              <div className="field !mb-2"><label>Other Costs ($/yr)</label><input type="number" value={other} onChange={e=>setOther(+e.target.value||0)}/></div>
              <div className="font-bold text-slate-900 mt-3 mb-1">Annual Tax & Cost Increase</div>
              <div className="field !mb-2"><label>Property Taxes Increase</label><div className="flex gap-2"><input type="number" step="0.01" value={vaTaxInc} onChange={e=>setVaTaxInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
              <div className="field !mb-2"><label>Home Insurance Increase</label><div className="flex gap-2"><input type="number" step="0.01" value={vaInsInc} onChange={e=>setVaInsInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
              <div className="field !mb-2"><label>HOA Fee Increase</label><div className="flex gap-2"><input type="number" step="0.01" value={vaHoaInc} onChange={e=>setVaHoaInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
              <div className="field !mb-2"><label>Other Costs Increase</label><div className="flex gap-2"><input type="number" step="0.01" value={vaOthInc} onChange={e=>setVaOthInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
              <div className="font-bold text-slate-900 mt-3 mb-1">Extra Payments</div>
              <div className="field !mb-2"><label>Extra Monthly Pay</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={vaExtraM} onChange={e=>setVaExtraM(+e.target.value||0)} className="flex-1" data-testid="va-extra-m"/></div></div>
              <div className="field !mb-2"><label>Extra Yearly Pay</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={vaExtraY} onChange={e=>setVaExtraY(+e.target.value||0)} className="flex-1"/></div></div>
              <div className="field !mb-2"><label>Extra One-time Pay</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={vaExtraOne} onChange={e=>setVaExtraOne(+e.target.value||0)} className="flex-1"/></div></div>
              <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={vaBiweekly} onChange={e=>setVaBiweekly(e.target.checked)}/> Show Biweekly Payback Results</label>
            </div>}
          </div>
          <div>
            <div className="result-panel mb-4"><div className="result-title">Monthly Pay</div><div className="result-value" data-testid="va-monthly">{fmt(c.monthlyTotal)}</div></div>
            <PieChart parts={[{label:'Principal',value:c.baseLoan,color:'#2563eb'},{label:'Interest',value:c.amort.totalInterest,color:'#10b981'}]} size={180}/>
            <table className="data-table mt-3"><tbody>
              <tr><td>House Price</td><td className="text-right font-semibold">{fmt(homePrice)}</td></tr>
              <tr><td>VA Funding Fee ({c.ffPct}%)</td><td className="text-right">{fmt(c.ffAmt)}</td></tr>
              <tr><td>Down Payment</td><td className="text-right">{fmt(c.downAmt)}</td></tr>
              <tr><td>Loan Amount</td><td className="text-right font-semibold">{fmt(c.loan)}</td></tr>
              <tr><td>Total of {term*12} Mortgage Payments</td><td className="text-right">{fmt(c.amort.totalPaid)}</td></tr>
              <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.amort.totalInterest)}</td></tr>
            </tbody></table>
          </div>
        </div>
        <StepsPanel steps={[
          `Down payment = ${fmt(c.downAmt)} (${c.downPct.toFixed(2)}%)`,
          `VA Funding Fee = ${c.ffPct}% × ${fmt(c.baseLoan)} = ${fmt(c.ffAmt)} (${fundingFee === 'financed'?'financed into loan':'paid upfront'})`,
          `Loan Amount = ${fmt(c.loan)} → Monthly P&I = <b>${fmt(c.amort.M)}</b>`,
        ]}/>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c.amort}/></div>
          <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={c.loan}/></div>
        </div>
      </CalcShell>
    </>
  );
}
